import React, { useMemo } from 'react';
import {
  Grid, Paper, Typography, Stack, Box, Divider, Card, CardContent,
  LinearProgress, Chip, Avatar, List, ListItem, ListItemText
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';

const SubjectStatisticsTab = ({ subject, schedules, subjectColor }) => {
  // Calculate all statistics
  const stats = useMemo(() => {
    if (!schedules || schedules.length === 0) {
      return {
        totalSchedules: 0,
        totalHours: 0,
        totalMinutes: 0,
        uniqueClasses: [],
        uniqueDays: [],
        schedulesByClass: {},
        schedulesByDay: {},
        schedulesByTeacher: {},
        averageDuration: 0,
        mostBusyDay: null,
        mostBusyClass: null
      };
    }

    const uniqueClasses = [...new Set(schedules.map(s => s.school_class_name).filter(Boolean))];
    const uniqueDays = [...new Set(schedules.map(s => s.day_of_week).filter(Boolean))];
    const totalMinutes = schedules.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    const totalHours = totalMinutes / 60;

    // Group by class
    const schedulesByClass = {};
    schedules.forEach(schedule => {
      const className = schedule.school_class_name || 'غير محدد';
      if (!schedulesByClass[className]) {
        schedulesByClass[className] = [];
      }
      schedulesByClass[className].push(schedule);
    });

    // Group by day
    const schedulesByDay = {};
    schedules.forEach(schedule => {
      const day = schedule.day_of_week;
      if (!schedulesByDay[day]) {
        schedulesByDay[day] = [];
      }
      schedulesByDay[day].push(schedule);
    });

    // Group by teacher
    const schedulesByTeacher = {};
    schedules.forEach(schedule => {
      const teacherName = schedule.teacher_name || 'غير محدد';
      if (!schedulesByTeacher[teacherName]) {
        schedulesByTeacher[teacherName] = [];
      }
      schedulesByTeacher[teacherName].push(schedule);
    });

    // Find most busy day
    const mostBusyDay = Object.entries(schedulesByDay)
      .sort((a, b) => b[1].length - a[1].length)[0]?.[0] || null;

    // Find most busy class
    const mostBusyClass = Object.entries(schedulesByClass)
      .sort((a, b) => b[1].length - a[1].length)[0]?.[0] || null;

    return {
      totalSchedules: schedules.length,
      totalHours: totalHours.toFixed(1),
      totalMinutes,
      uniqueClasses,
      uniqueDays,
      schedulesByClass,
      schedulesByDay,
      schedulesByTeacher,
      averageDuration: (totalMinutes / schedules.length).toFixed(0),
      mostBusyDay,
      mostBusyClass
    };
  }, [schedules]);

  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  return (
    <Grid container spacing={3}>
      {/* Key Statistics */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: subjectColor, mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <CalendarTodayIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color={subjectColor}>
            {stats.totalSchedules}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            إجمالي الحصص
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <AccessTimeIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {stats.totalHours}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ساعة أسبوعياً
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <SchoolIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="info.main">
            {stats.uniqueClasses.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            صفوف دراسية
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 2 }}>
          <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1, width: 56, height: 56 }}>
            <BarChartIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="warning.main">
            {stats.averageDuration}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            متوسط المدة (دقيقة)
          </Typography>
        </Card>
      </Grid>

      {/* Distribution by Class */}
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            التوزيع حسب الصف
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {Object.keys(stats.schedulesByClass).length > 0 ? (
            <Stack spacing={2}>
              {Object.entries(stats.schedulesByClass)
                .sort((a, b) => b[1].length - a[1].length)
                .map(([className, classSchedules]) => {
                  const percentage = (classSchedules.length / stats.totalSchedules) * 100;
                  return (
                    <Box key={className}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body1" fontWeight="medium">
                          {className}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${classSchedules.length} حصة`}
                          sx={{ bgcolor: `${subjectColor}20`, color: subjectColor }}
                        />
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: `${subjectColor}10`,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: subjectColor,
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  );
                })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد بيانات
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Distribution by Day */}
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            التوزيع حسب اليوم
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {Object.keys(stats.schedulesByDay).length > 0 ? (
            <Stack spacing={2}>
              {Object.entries(stats.schedulesByDay)
                .sort((a, b) => {
                  const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                  return dayOrder.indexOf(a[0]) - dayOrder.indexOf(b[0]);
                })
                .map(([day, daySchedules]) => {
                  const percentage = (daySchedules.length / stats.totalSchedules) * 100;
                  return (
                    <Box key={day}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body1" fontWeight="medium">
                          {getDayName(day)}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${daySchedules.length} حصة`}
                          sx={{ bgcolor: `${subjectColor}20`, color: subjectColor }}
                        />
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: `${subjectColor}10`,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: subjectColor,
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  );
                })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد بيانات
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Additional Insights */}
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            معلومات إضافية
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {stats.mostBusyDay && (
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={0} sx={{ bgcolor: `${subjectColor}10`, p: 2, borderRadius: 2 }}>
                  <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                    <TrendingUpIcon sx={{ color: subjectColor }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        أكثر يوم ازدحاماً
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={subjectColor}>
                        {getDayName(stats.mostBusyDay)}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            )}
            {stats.mostBusyClass && (
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={0} sx={{ bgcolor: `${subjectColor}10`, p: 2, borderRadius: 2 }}>
                  <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                    <SchoolIcon sx={{ color: subjectColor }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        أكثر صف ازدحاماً
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={subjectColor}>
                        {stats.mostBusyClass}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            )}
            {stats.uniqueDays.length > 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={0} sx={{ bgcolor: `${subjectColor}10`, p: 2, borderRadius: 2 }}>
                  <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                    <CalendarTodayIcon sx={{ color: subjectColor }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        أيام التدريس
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={subjectColor}>
                        {stats.uniqueDays.length} يوم
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SubjectStatisticsTab;
